import { applyScopeFiltersToBody, mapPaginatedResponse, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  CreateCustomer,
  Customer,
  CustomerStatus,
  CustomerType,
  CustomerWithResolvedRefs,
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
  SortParams,
} from '@klareospace/pharos-lib-types';

import type { CustomerSortableColumn } from '../types/customer.types';

// ─── Param ───────────────────────────────────────────────────────────────────

export interface SearchCustomersParams {
  page?: number;
  pageSize?: number;
  search?: string;
  siteId?: string;
  siteIds?: string[];
  sort?: SortParams<CustomerSortableColumn>;
  spaceId?: string;
  spaceIds?: string[];
  statuses?: CustomerStatus[];
  types?: CustomerType[];
}

// ─── Result Types ─────────────────────────────────────────────────────────────

export type CustomerSearchResult = PaginatedResponse<CustomerWithResolvedRefs>;

// ─── API ──────────────────────────────────────────────────────────────────────

export interface CustomerApi {
  createCustomer: (params: CreateCustomer) => Promise<Customer>;
  searchCustomers: (params: SearchCustomersParams) => Promise<CustomerSearchResult>;
  updateCustomer: (id: string, params: Partial<CreateCustomer>) => Promise<Customer>;
}

export function createCustomerApi(client: ApiClient): CustomerApi {
  return {
    createCustomer: async (params: CreateCustomer): Promise<Customer> => {
      const response = await client.execute<PharosApiResponse<Customer>>(() =>
        client.instance.post('/customers', params),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchCustomers: async (params: SearchCustomersParams): Promise<CustomerSearchResult> => {
      const body = toSearchBody(params);
      const query: Record<string, number | string | undefined> = {
        limit: params.pageSize,
        page: params.page,
      };
      if (params.sort) {
        query.sort = params.sort.sortBy;
        query.sortDirection = params.sort.sortDirection ?? undefined;
      }
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<CustomerWithResolvedRefs>>>(() =>
        client.instance.post('/customers/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },

    updateCustomer: async (id: string, params: Partial<CreateCustomer>): Promise<Customer> => {
      const response = await client.execute<PharosApiResponse<Customer>>(() =>
        client.instance.patch(`/customers/${id}`, params),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchCustomersParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) body['search'] = trimmedSearch;
  applyScopeFiltersToBody(body, params);
  if (params.statuses && params.statuses.length > 0) body['statuses'] = params.statuses;
  if (params.types && params.types.length > 0) body['types'] = params.types;
  return body;
}
